/**
 * McpCard — generic async card owning the four states every live-data surface
 * needs: loading skeleton / desktop-only (web build) / error-with-reason / data.
 */

import { ReactNode } from "react";
import { Monitor, RefreshCw, AlertTriangle, KeyRound } from "lucide-react";
import { McpFailReason, describeFailure } from "../lib/mcp";
import { Link } from "react-router-dom";

interface McpCardProps {
  title: string;
  loading: boolean;
  error: { reason: McpFailReason; detail?: string } | null;
  onRefresh?: () => void;
  accentVar?: string; // e.g. "--biome-finance" → tinted left border per §5.1
  children: ReactNode; // rendered only in the data state
}

export default function McpCard({ title, loading, error, onRefresh, accentVar, children }: McpCardProps) {
  return (
    <div
      className="rounded-xl border bg-surface p-4 animate-fade-in"
      style={accentVar ? { borderLeft: `3px solid var(${accentVar})` } : undefined}
    >
      <div className="flex items-center mb-3">
        <div className="text-caption uppercase tracking-wide text-text-muted">{title}</div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            title="Refresh"
            className="ml-auto p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        )}
      </div>
      {loading ? (
        <div>
          <div className="skeleton skeleton-text" />
          <div className="skeleton skeleton-text" />
          <div className="skeleton skeleton-text skeleton-text--short" />
        </div>
      ) : error ? (
        <ErrorState reason={error.reason} detail={error.detail} />
      ) : (
        children
      )}
    </div>
  );
}

function ErrorState({ reason, detail }: { reason: McpFailReason; detail?: string }) {
  if (reason === "web-unsupported") {
    return (
      <div className="flex items-center gap-2 text-body-sm text-text-dim py-2">
        <Monitor size={16} />
        <span>Live SAGE data requires the desktop app.</span>
      </div>
    );
  }
  if (reason === "no-token") {
    return (
      <div className="flex items-center gap-2 text-body-sm text-warning py-2">
        <KeyRound size={16} />
        <span>
          No SAGE token — paste it in{" "}
          <Link to="/settings" className="underline text-primary hover:text-primary-hover">
            Settings → Connections
          </Link>
          .
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 text-body-sm text-danger py-2">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <span>{describeFailure(reason, detail)}</span>
    </div>
  );
}
